import keycloak from '../keycloak';

/**
 * Normalize an org or role name to a comparable slug.
 * Lowercases, strips diacritics (å→a, ä→a, ö→o etc.) and replaces
 * spaces and underscores with a single underscore.
 * e.g. "Region Väst", "Region_Väst", "region_vast" → all → "region_vast"
 */
export const normalizeOrgSlug = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s_]+/g, '_');
};

const ROLE_SUFFIX_LABELS = {
    'tester': 'Testare',
    'administrator': 'Administratör',
    'reviewer': 'Granskare',
    'e2e-reviewer': 'E2E-Granskare',
    'e2e-tester': 'E2E-Testare',
};

/**
 * Returns a human-readable Swedish label for a raw Keycloak role name.
 * Strips the org prefix (everything before and including the last '-' that
 * precedes a known suffix) and maps the suffix to a display label.
 * e.g. "RegionA-tester" → "Testare", "inera-administrator" → "Administratör"
 * Falls back to the raw role name if no mapping is found.
 */
export const formatRoleName = (roleId) => {
    if (!roleId) return '';
    // Try longest-match suffix first (handles "e2e-reviewer" before "reviewer")
    const lower = roleId.toLowerCase();
    for (const [suffix, label] of Object.entries(ROLE_SUFFIX_LABELS).sort((a, b) => b[0].length - a[0].length)) {
        if (lower.endsWith('-' + suffix) || lower === suffix) {
            return label;
        }
    }
    // Fallback: return the part after the last hyphen, capitalized
    const lastHyphen = roleId.lastIndexOf('-');
    if (lastHyphen !== -1) {
        const suffix = roleId.slice(lastHyphen + 1);
        return suffix.charAt(0).toUpperCase() + suffix.slice(1);
    }
    return roleId;
};

/**
 * Notified whenever getValidToken() actually refreshes the Keycloak token.
 * AuthContext subscribes so its `token` state (which OrganisationContext decodes
 * org/role data from) doesn't go stale relative to the real keycloak.token —
 * without this, a background API call can silently rotate keycloak.token while
 * AuthContext's own copy (only updated via its activity-gated refresh) keeps
 * referencing the old one.
 */
const tokenRefreshListeners = new Set();

export const onTokenRefreshed = (listener) => {
    tokenRefreshListeners.add(listener);
    return () => tokenRefreshListeners.delete(listener);
};

/**
 * Get a valid Keycloak token, refreshing it if necessary.
 * Returns the current token or null if authentication fails.
 */
export const getValidToken = async () => {
    try {
        // Check if keycloak is initialized and authenticated
        if (!keycloak.authenticated) {
            console.error('Keycloak is not authenticated');
            // Try to login if not authenticated
            keycloak.login();
            return null;
        }

        // Refresh token if it expires in less than 30 seconds
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
            console.log('Token was refreshed');
            tokenRefreshListeners.forEach((listener) => listener(keycloak.token));
        }
        return keycloak.token;
    } catch (error) {
        console.error('Failed to refresh token', error);
        // Token refresh failed, user needs to re-login
        keycloak.login();
        return null;
    }
};

/**
 * Wrapper for fetch that automatically includes authentication token
 * and handles common error cases.
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
    const token = await getValidToken();
    
    if (!token) {
        throw new Error('Ingen giltig inloggningssession. Logga in på nytt.');
    }

    const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(url, {
        ...options,
        headers,
    });

    if (!response.ok) {
        if (response.status === 401) {
            console.error('Authentication failed - token may have expired');
            // Try to refresh token and login if needed
            keycloak.login();
            throw new Error(`API failed (401): Unauthorized - please log in again`);
        }
        throw new Error(`API failed (${response.status}): ${response.statusText}`);
    }

    return response;
};